/**
 * Koi Rewards page - Growave adapter
 * ==================================
 * The ONLY file on the rewards page that knows Growave exists. Everything else
 * is theme-owned markup. Replaces snippets/rewards-spending-inject.liquid
 * (382 lines of Stamped DOM surgery) for templates/page.rewards.json.
 *
 * Responsibilities:
 *   1. Copy the points balance out of Growave's hidden "Loyalty info" block
 *      into the theme's summary slot
 *   2. Smooth-scroll the page CTAs to their Growave widget sections
 *   3. Provide one registration point for styling inside Growave's shadow roots
 *
 * The popup is NOT opened from JS. Growave support confirmed the supported way
 * is the `#rewards-home` deep link -- any plain <a href="/#rewards-home"> opens
 * the rewards popup (logged in: shows their account; logged out: prompts to log
 * in or sign up). We deliberately do not intercept those clicks.
 *
 * WHERE THE BALANCE COMES FROM
 * Growave's "Loyalty info" app block is on this template but visually hidden
 * (growave-rewards.css). It renders the real balance; we read it and fill the
 * theme's own summary so the page keeps its original design.
 *
 * This couples us to Growave's rendered markup, which is a known liability --
 * an app update can change it. The read is anchored on the visible "Your
 * points" label rather than CSS classes (their classes carry a Svelte build
 * hash), and the summary slot stays hidden when the read fails, so the failure
 * mode is a missing number rather than a wrong one.
 *
 * OPENING THE POPUP FROM JS
 * `gw.sdk.rewards.methods.openRewardsPopup()` -- see initAutoOpen(). This file
 * only loads on page.rewards (layout/theme.liquid), so it is already scoped to
 * the rewards page.
 *
 * An earlier version of this comment claimed there was no SDK route, because
 * `gw.sdk` exposes no `rewards` property until the module is imported. Wrong on
 * two counts: `runIfModuleReady` is the public accessor, and the opener lives
 * under `.methods`, not directly on `gw.sdk.rewards`. Corrected Aug 2026.
 *
 * IF THE BALANCE STOPS SHOWING: run `window.KoiRewardsAPI.probeLoyaltyInfo()`
 * in the console to print the widget's current markup, then update
 * POINTS_LABEL / LOYALTY_HOST_SELECTOR below to match.
 *
 * IF A CTA STOPS SCROLLING: run `window.KoiRewardsAPI.probeAnchors()`. Growave's
 * widgets are `apps` sections, which cannot carry an id from template JSON, so
 * tagWidgetAnchors() stamps one onto each placeholder at init. If the probe
 * reports a hash matching NOTHING, Growave renamed the placeholder class --
 * update FALLBACK_TARGETS.
 */

(function () {
  'use strict';

  var CONFIG = {
    // Fallback timeout for the `gwSdkReady` event, in case the app embed is off
    // and the event never fires.
    GW_TIMEOUT_MS: 10000,
    GW_POLL_MS: 200,
    BALANCE_RETRY_MS: 400,
    BALANCE_MAX_RETRIES: 25,
    SCROLL_OFFSET: 100,
    // Growave deep links. Any href ending in one of these is left alone by the
    // click handler so the browser sets the hash and Growave reacts to it.
    //
    // #rewards-home is confirmed by Growave support: "add #rewards-home to the
    // end of the URL assigned to your button". Logged in it opens the account;
    // logged out it prompts to log in or sign up.
    //
    // This is an explicit allow-list rather than a "#rewards-*" pattern on
    // purpose: #rewards-summary is one of OUR anchor IDs, and a pattern would
    // stop it scrolling. To add another view (referral, ways to spend), confirm
    // the hash Growave actually uses and append it here.
    POPUP_HASHES: ['#rewards-home'],
    SELECTORS: {
      balanceValue: '[data-koi-rewards-balance]',
      balanceWrapper: '[data-koi-rewards-balance-wrapper]',
      action: '[data-koi-rewards-action]',
      ctaButton: '.koi-rewards-cta__button'
    }
  };

  // ============================================
  // UTILITIES
  // ============================================

  function log(message, detail) {
    if (!window.KoiRewardsDebug) return;
    console.log('[koi-rewards] ' + message, detail !== undefined ? detail : '');
  }

  /**
   * Resolves with `window.gw.sdk` once Growave's SDK is ready, or rejects on
   * timeout.
   *
   * Growave documents the SDK as initialising automatically and announcing
   * itself with the `gwSdkReady` event -- the object to use is `window.gw.sdk`,
   * NOT `window.gw`. `window.gw` can exist before the SDK is usable, so an
   * earlier version of this file that polled for `window.gw` was reading a
   * half-built object. Poll only as a backstop for a missed event.
   *
   * If this rejects, the most likely cause is the Growave app embed being
   * switched off: Theme editor > App embeds > Growave > ON.
   */
  function waitForGrowaveSdk() {
    return new Promise(function (resolve, reject) {
      if (window.gw && window.gw.sdk) {
        resolve(window.gw.sdk);
        return;
      }

      var settled = false;
      var timer = null;

      function finish() {
        if (settled) return false;
        if (!(window.gw && window.gw.sdk)) return false;
        settled = true;
        document.removeEventListener('gwSdkReady', finish);
        if (timer) clearInterval(timer);
        resolve(window.gw.sdk);
        return true;
      }

      document.addEventListener('gwSdkReady', finish);

      var waited = 0;
      timer = setInterval(function () {
        if (finish()) return;
        waited += CONFIG.GW_POLL_MS;
        if (waited >= CONFIG.GW_TIMEOUT_MS) {
          settled = true;
          clearInterval(timer);
          document.removeEventListener('gwSdkReady', finish);
          reject(new Error('window.gw.sdk never became ready -- is the Growave app embed on?'));
        }
      }, CONFIG.GW_POLL_MS);
    });
  }

  /** Pulls the first integer out of a value, e.g. "1,240 points" -> 1240. */
  function parsePoints(value) {
    if (typeof value === 'number' && isFinite(value)) return Math.floor(value);
    if (typeof value !== 'string') return null;
    var match = value.replace(/,/g, '').match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  // ============================================
  // BALANCE RESOLUTION
  // ============================================
  // The balance comes from Growave's own "Loyalty info" app block, which is
  // placed on this template but hidden (see growave-rewards.css). Growave
  // renders the real number there; we copy it into the theme's summary slot so
  // the page keeps its original design.
  //
  // Why not the SDK: `gw.sdk` has no `rewards` property. The Rewards module is
  // lazily imported through `_sdkDynamicModulesManager` and never exposed as a
  // public accessor, so there is no supported call that returns the balance.
  // Verified in console on the preview theme, Aug 2026.

  // Matched with a prefix so the Svelte build hash in the full class name
  // cannot break it.
  var LOYALTY_HOST_SELECTOR = '[class*="gw-rd-loyalty-info"]';

  // The value element carries the SAME class as the widget's own "Loyalty info"
  // heading, so class cannot identify it. Anchor on the visible label instead
  // and take the next numeric element -- resilient to Growave restyling.
  var POINTS_LABEL = /^your points$/i;

  /**
   * Reads the points figure out of the hidden Loyalty info widget.
   *
   * Deliberately scoped to that one widget's shadow root. An earlier version
   * scanned every shadow root on the page for `[class*="points"]` and could
   * match the ways-to-spend widget's "Earn 50 more points to unlock" copy,
   * which parses to a plausible-looking but entirely wrong number.
   */
  function readLoyaltyInfoPoints() {
    var host = document.querySelector(LOYALTY_HOST_SELECTOR);
    if (!host || !host.shadowRoot) return null;

    var nodes = host.shadowRoot.querySelectorAll('*');
    var seenLabel = false;

    for (var i = 0; i < nodes.length; i++) {
      var text = ownText(nodes[i]);
      if (!text) continue;

      if (!seenLabel) {
        if (POINTS_LABEL.test(text)) seenLabel = true;
        continue;
      }

      // First bare number after the label. Guard against "(expiration: Aug 20,
      // 2027)" and "Earn 50 points more" by requiring the whole string to be
      // digits -- those have surrounding words and are skipped.
      if (/^\d[\d,]*$/.test(text)) return parsePoints(text);
    }

    return null;
  }

  /** An element's own text, ignoring text contributed by its descendants. */
  function ownText(node) {
    var text = '';
    for (var i = 0; i < node.childNodes.length; i++) {
      if (node.childNodes[i].nodeType === 3) text += node.childNodes[i].nodeValue;
    }
    return text.trim();
  }

  // Ordered list of ways to obtain the balance. Each returns a number, or null
  // if that shape is not available. First non-null wins.
  var BALANCE_STRATEGIES = [
    {
      // Preferred if it ever exists: a Liquid-rendered value written into the
      // page server-side. No flash, no polling, no coupling to Growave markup.
      // Worth revisiting -- ask Growave support about a customer metafield.
      name: 'liquid-metafield',
      run: function () {
        if (!window.KoiRewards) return null;
        return parsePoints(window.KoiRewards.balance);
      }
    },
    {
      name: 'loyalty-info-widget',
      run: readLoyaltyInfoPoints
    }
  ];

  function resolveBalance() {
    for (var i = 0; i < BALANCE_STRATEGIES.length; i++) {
      var strategy = BALANCE_STRATEGIES[i];
      var value = null;
      try {
        value = strategy.run();
      } catch (error) {
        log('strategy threw: ' + strategy.name, error);
      }
      if (value !== null && value !== undefined && !isNaN(value)) {
        log('balance resolved via ' + strategy.name, value);
        return value;
      }
    }
    return null;
  }

  function renderBalance(value) {
    var valueEl = document.querySelector(CONFIG.SELECTORS.balanceValue);
    var wrapperEl = document.querySelector(CONFIG.SELECTORS.balanceWrapper);
    if (!valueEl || !wrapperEl) return;

    valueEl.textContent = value.toLocaleString();
    wrapperEl.removeAttribute('hidden');
  }

  /**
   * The hidden widget is duplicate content for anyone using a screen reader --
   * they get the balance from the summary section. Marked here rather than in
   * CSS because it is a semantic concern, and the host only exists once
   * Growave has injected it.
   */
  function hideLoyaltyHostFromA11y() {
    var host = document.querySelector(LOYALTY_HOST_SELECTOR);
    if (host && !host.hasAttribute('aria-hidden')) {
      host.setAttribute('aria-hidden', 'true');
    }
  }

  function initBalance() {
    // The summary section is Liquid-gated to logged-in customers, so if the slot
    // is absent there is nothing to do.
    if (!document.querySelector(CONFIG.SELECTORS.balanceWrapper)) return;

    // Try immediately in case a server-rendered value is already present.
    var immediate = resolveBalance();
    if (immediate !== null) {
      hideLoyaltyHostFromA11y();
      renderBalance(immediate);
      return;
    }

    waitForGrowaveSdk()
      .then(function () {
        // Growave hydrates its widgets asynchronously even after the SDK is
        // ready, and the shadow root does not exist until it does -- so retry
        // on an interval rather than reading once.
        var attempts = 0;
        var timer = setInterval(function () {
          hideLoyaltyHostFromA11y();

          var value = resolveBalance();
          if (value !== null) {
            clearInterval(timer);
            renderBalance(value);
            watchForBalanceChanges();
            return;
          }

          attempts += 1;
          if (attempts >= CONFIG.BALANCE_MAX_RETRIES) {
            clearInterval(timer);
            // Slot stays hidden: a missing number is a better failure than a
            // wrong one. Run probeLoyaltyInfo() to see what changed.
            log('balance unresolved after ' + attempts + ' attempts -- slot stays hidden');
          }
        }, CONFIG.BALANCE_RETRY_MS);
      })
      .catch(function (error) {
        log('giving up on balance', error);
      });
  }

  /**
   * Keeps the summary in step if Growave re-renders the widget -- e.g. after a
   * customer redeems a reward in the popup without a page reload.
   */
  function watchForBalanceChanges() {
    if (typeof MutationObserver !== 'function') return;

    var host = document.querySelector(LOYALTY_HOST_SELECTOR);
    if (!host || !host.shadowRoot) return;

    var observer = new MutationObserver(function () {
      var value = resolveBalance();
      if (value !== null) renderBalance(value);
    });

    observer.observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  // ============================================
  // NAVIGATION / CTAs
  // ============================================

  // If an anchor section gets deleted in the theme editor, fall back to locating
  // the Growave widget itself so the CTAs keep working.
  var FALLBACK_TARGETS = {
    '#growave-referral': '[class*="gw-rd-referral"]',
    '#growave-ways-to-spend': '[class*="gw-rd-ways-to-spend"]',
    '#growave-history': '[class*="gw-rd-history"]',
    '#growave-vip-tiers': '[class*="gw-rd-vip"]'
  };

  /**
   * Stamps each FALLBACK_TARGETS id onto the Growave placeholder it points at.
   *
   * Growave's widgets are `apps` sections, and an `apps` section cannot be
   * given an id from template JSON -- so nothing on the page actually has
   * id="growave-ways-to-spend". Before this ran, three things were quietly
   * broken as a result:
   *   - scrollToAnchor only worked via the FALLBACK_TARGETS lookup below, so a
   *     rename of Growave's placeholder class would kill the CTAs outright
   *   - the scroll-margin-top rules in growave-rewards.css are keyed on these
   *     ids, so the sticky header overlapped the widget on arrival
   *   - a plain <a href="#growave-ways-to-spend"> did nothing at all, which is
   *     the obvious thing for the next person to reach for
   *
   * Giving the placeholder the real id fixes all three at once and makes the
   * fallback lookup what it was named for -- a fallback -- rather than the only
   * path. Growave does not read this id, so adding it is inert on their side.
   */
  function tagWidgetAnchors() {
    var missing = 0;

    Object.keys(FALLBACK_TARGETS).forEach(function (hash) {
      var id = hash.slice(1);
      if (document.getElementById(id)) return;

      var el = document.querySelector(FALLBACK_TARGETS[hash]);
      if (el) {
        el.id = id;
        log('tagged anchor', id);
      } else {
        // Not every widget is on every build of the page -- VIP tiers in
        // particular is not placed. Absent is normal, not an error.
        missing += 1;
      }
    });

    return missing === 0;
  }

  /**
   * Growave injects its placeholders during hydration on some builds, so the
   * first pass can run before they exist. Retry on the same cadence the balance
   * read uses, then stop -- the fallback lookup still covers whatever is late.
   */
  function initWidgetAnchors() {
    if (tagWidgetAnchors()) return;

    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (tagWidgetAnchors() || attempts >= CONFIG.BALANCE_MAX_RETRIES) {
        clearInterval(timer);
      }
    }, CONFIG.BALANCE_RETRY_MS);
  }

  function findTarget(hash) {
    try {
      var direct = document.querySelector(hash);
      if (direct) return direct;
    } catch (error) {
      log('invalid anchor selector', hash);
      return null;
    }

    var fallback = FALLBACK_TARGETS[hash];
    if (fallback) {
      var widget = document.querySelector(fallback);
      if (widget) {
        log('anchor missing, using widget fallback', fallback);
        return widget;
      }
    }
    return null;
  }

  function scrollToAnchor(hash) {
    var target = findTarget(hash);
    if (!target) {
      log('scroll target not found', hash);
      return false;
    }
    var top = target.getBoundingClientRect().top + window.pageYOffset - CONFIG.SCROLL_OFFSET;
    window.scrollTo({ top: top, behavior: 'smooth' });
    return true;
  }

  /**
   * Opens the Growave rewards popup by setting the documented deep-link hash.
   * Growave support: "add #rewards-home to the end of the URL assigned to your
   * button" -- logged-in customers get their account, logged-out visitors get a
   * log in / sign up prompt.
   *
   * Only needed for programmatic callers; ordinary links reach the popup on
   * their own because handleActionClick deliberately leaves them alone.
   */
  /**
   * Growave's own launcher, which lives inside the popup widget's shadow root.
   * Clicking it is the only route that opens the popup WITHOUT a navigation --
   * the #rewards-home deep link is read on page load, so setting the hash while
   * already on the page changes the URL and nothing else.
   *
   * Selector list rather than one selector: Growave's launcher markup is not
   * documented and we are matching their internals, so try the plausible shapes
   * and take the first that exists. Update LAUNCHER_SELECTORS if it moves.
   */
  // Confirmed on the live theme:
  //   div.gw-rd-popup-widget-launcher            <- wrapper, NOT clickable
  //     button.gw-launcher.gw-launcher--size-big <- the real trigger
  //       span.gw-launcher__icon
  // Ordered most to least specific, and every entry resolves to a <button>:
  // matching the wrapper div would click a non-interactive element and do
  // nothing, which is what a naive [class*="launcher"] would have done.
  var LAUNCHER_SELECTORS = [
    'button.gw-launcher',
    '[class*="gw-rd-popup-widget-launcher"] button',
    'button[class*="launcher"]',
    'button[aria-label*="Rewards"]',
    'button'
  ];

  function findGrowaveLauncher() {
    var host = document.querySelector('[class*="gw-rd-popup-widget"]');
    if (!host) return null;

    var root = host.shadowRoot || host;
    for (var i = 0; i < LAUNCHER_SELECTORS.length; i++) {
      var el = root.querySelector(LAUNCHER_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  /**
   * Opens the rewards popup, preferring the launcher click so the customer
   * stays on the page. Falls back to the documented deep link, which Growave
   * support confirmed works but reads the hash on load.
   */
  function openGrowavePopup(hash) {
    var launcher = findGrowaveLauncher();
    if (launcher) {
      launcher.click();
      log('opened popup via launcher click', launcher.className);
      return true;
    }

    var target = hash || CONFIG.POPUP_HASHES[0];

    if (window.location.hash === target) {
      // Re-assigning an identical hash fires no event, so clear it first.
      window.location.hash = '';
    }
    window.location.hash = target;
    log('launcher not found -- fell back to deep link', target);
    return true;
  }

  /** True if `href` is (or ends with) one of Growave's popup deep links. */
  function isPopupLink(href) {
    for (var i = 0; i < CONFIG.POPUP_HASHES.length; i++) {
      var hash = CONFIG.POPUP_HASHES[i];
      if (href === hash || href.slice(-hash.length) === hash) return true;
    }
    return false;
  }

  function handleActionClick(event) {
    if (!event.target || !event.target.closest) return;

    var link = event.target.closest(
      CONFIG.SELECTORS.action + ', ' + CONFIG.SELECTORS.ctaButton
    );
    if (!link) return;

    var href = link.getAttribute('href') || '';

    // Popup deep links are intercepted ONLY when the launcher is available --
    // clicking it keeps the customer on the page. With no launcher we hand the
    // click off untouched so the browser sets the hash, which is the mechanism
    // Growave documents.
    if (isPopupLink(href)) {
      if (findGrowaveLauncher()) {
        event.preventDefault();
        openGrowavePopup(href);
      }
      return;
    }

    if (href.charAt(0) === '#' && href.length > 1) {
      // Only take over if the target actually exists; otherwise let the browser
      // do whatever it would normally do.
      if (scrollToAnchor(href)) event.preventDefault();
    }
  }

  function initActions() {
    document.addEventListener('click', handleActionClick);
  }

  // ============================================
  // GROWAVE SHADOW-ROOT STYLING HOOK
  // ============================================
  // External CSS cannot cross Growave's shadow boundary. This is the sanctioned
  // way in: styleElement is injected inside the widget's shadow root. Left
  // intentionally empty -- add rules here only for what genuinely cannot be
  // handled by Growave admin > Branding or the app block's Custom CSS box.

  var SHADOW_CSS = [
    // '.gw-rd-ways-to-spend-page-widget { font-family: "PP Agrandir", sans-serif; }'
  ].join('\n');

  function initShadowStyling() {
    if (!SHADOW_CSS.trim()) return;

    waitForGrowaveSdk()
      .then(function (sdk) {
        // VERIFIED on the preview theme: `registerWidgetCustomization` is on
        // `window.gw`, not on `window.gw.sdk`. Keep the sdk check first anyway
        // in case they move it when the SDK stops being beta.
        var host = typeof sdk.registerWidgetCustomization === 'function'
          ? sdk
          : window.gw;

        if (!host || typeof host.registerWidgetCustomization !== 'function') {
          log('registerWidgetCustomization unavailable');
          return;
        }

        host.registerWidgetCustomization(
          // Match the rewards page widgets we place on this template.
          function (widgetConfig) {
            var name = (widgetConfig && widgetConfig.name) || '';
            return name.indexOf('Rewards') === 0;
          },
          function () {
            var style = document.createElement('style');
            style.textContent = SHADOW_CSS;
            return { styleElement: style };
          }
        );
      })
      .catch(function (error) {
        log('shadow styling skipped', error);
      });
  }

  // ============================================
  // AUTO-OPEN
  // ============================================

  /**
   * Opens the rewards popup on page load.
   *
   * `rewards.methods.openRewardsPopup()` is the real opener -- NOT
   * `rewards.openRewardsPopup()`, which does not exist. Verified Aug 2026.
   *
   * WHY THIS RETRIES
   * Module-ready is not widget-ready. runIfModuleReady fires from inside
   * Growave's own registerModule(), at which point the popup widget has not yet
   * subscribed to the method channel, and the call rejects with:
   *
   *     Method execution failed: no subscribers found
   *
   * That is a timing condition, not a bad call, so we re-attempt until the
   * widget subscribes. WIDGET_INJECTED would be the precise signal, but it is
   * one-shot with no replay and Growave injects the popup before this deferred
   * file parses -- subscribing to it never fired in practice.
   *
   * The `#rewards-home` deep link cannot do this job: Growave reads that hash at
   * load time, so setting it on an already-open page changes the URL only.
   */
  function initAutoOpen() {
    var RETRY_MS = 300;
    var MAX_WAIT_MS = 15000;
    var waited = 0;

    function retry(reason) {
      waited += RETRY_MS;
      if (waited > MAX_WAIT_MS) {
        console.warn('[koi-rewards] auto-open gave up after ' + MAX_WAIT_MS + 'ms:', reason);
        return;
      }
      setTimeout(attempt, RETRY_MS);
    }

    function attempt() {
      var rewards = window.gw && window.gw.sdk && window.gw.sdk.rewards;
      var methods = rewards && rewards.methods;

      if (!methods || typeof methods.openRewardsPopup !== 'function') {
        retry('rewards.methods.openRewardsPopup missing');
        return;
      }

      methods
        .openRewardsPopup()
        .then(function (result) {
          log('auto-opened rewards popup after ' + waited + 'ms', result);
        })
        .catch(function (error) {
          retry(error && error.message ? error.message : error);
        });
    }

    function onSdkReady() {
      var sdk = window.gw.sdk;
      sdk.runIfModuleReady(sdk.common.constants.modules.REWARDS, attempt);
    }

    if (window.gw && window.gw.sdk) {
      onSdkReady();
    } else {
      document.addEventListener('gwSdkReady', onSdkReady);
    }
  }

  // ============================================
  // INIT
  // ============================================

  /**
   * Diagnostic for when the balance stops appearing. Prints the Loyalty info
   * widget's current markup so POINTS_LABEL / LOYALTY_HOST_SELECTOR can be
   * brought back in line with whatever Growave changed.
   */
  function probeLoyaltyInfo() {
    var host = document.querySelector(LOYALTY_HOST_SELECTOR);
    if (!host) {
      console.warn('[koi-rewards] no ' + LOYALTY_HOST_SELECTOR + ' on this page. Is the Loyalty info block placed on the template?');
      return null;
    }
    if (!host.shadowRoot) {
      console.warn('[koi-rewards] host found but it has no open shadow root -- Growave may have changed how it renders.');
      return null;
    }

    var lines = ['HOST: ' + host.tagName.toLowerCase() + '.' + host.className];
    var nodes = host.shadowRoot.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      var text = ownText(nodes[i]);
      if (text && text.length < 40) {
        lines.push('  ' + nodes[i].tagName.toLowerCase() + '.' + nodes[i].className + '  =>  ' + text);
      }
    }
    lines.push('readLoyaltyInfoPoints() returns: ' + readLoyaltyInfoPoints());

    var out = lines.join('\n');
    console.log(out);
    return out;
  }

  /**
   * Diagnostic for a CTA that does not scroll. Prints, for every anchor the
   * page uses, whether the id got stamped and what the fallback selector
   * currently matches -- which is enough to tell "Growave renamed the
   * placeholder" apart from "the widget is not on the template".
   */
  function probeAnchors() {
    var lines = [];
    Object.keys(FALLBACK_TARGETS).forEach(function (hash) {
      var byId = document.getElementById(hash.slice(1));
      var bySelector = document.querySelector(FALLBACK_TARGETS[hash]);
      lines.push(
        hash +
          '\n  id present : ' + (byId ? 'yes' : 'NO') +
          '\n  fallback   : ' + FALLBACK_TARGETS[hash] +
          '\n  matches    : ' + (bySelector ? bySelector.className : 'NOTHING') +
          '\n  scrollable : ' + (findTarget(hash) ? 'yes' : 'NO')
      );
    });
    var out = lines.join('\n');
    console.log(out);
    return out;
  }

  // Small public surface so section-level custom JavaScript in the theme editor
  // does not have to re-implement scrolling or popup opening.
  window.KoiRewardsAPI = {
    scrollTo: scrollToAnchor,
    openPopup: openGrowavePopup,
    findLauncher: findGrowaveLauncher,
    getBalance: resolveBalance,
    probeLoyaltyInfo: probeLoyaltyInfo,
    probeAnchors: probeAnchors
  };

  function init() {
    // Before initActions: a click landing between DOMContentLoaded and the
    // first tag pass still resolves through the fallback lookup, but tagging
    // first keeps the common case on the direct path.
    initWidgetAnchors();
    initActions();
    initBalance();
    initShadowStyling();
  }

  // Not inside init(): init() waits for DOMContentLoaded, and WIDGET_INJECTED is
  // a one-shot event with no replay. This script is `defer`red, so it parses
  // before DOMContentLoaded -- subscribing here rather than a tick later is what
  // keeps us from missing the event on a fast injection.
  // try/catch because this runs BEFORE init() is registered below: an exception
  // escaping here would stop the balance and CTA scrolling from ever wiring up,
  // turning an auto-open failure into a broken page.
  try {
    initAutoOpen();
  } catch (error) {
    console.warn('[koi-rewards] auto-open init failed', error);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
